import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { axiosClientCreditCore } from "../AxiosClient";
import { Section } from "./siigoStyledComponents";
import FailedOutboxRow from "./FailedOutboxRow";
import useSiigoErrorModal from "./useSiigoErrorModal";

export default function FailedOutboxTab({ notify }) {
  const [records, setRecords] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const { showError, errorModal } = useSiigoErrorModal();

  const fetchFailed = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await axiosClientCreditCore.get("/v1/siigo/outbox", {
        params: { status: "FAILED" },
      });
      setRecords(res.data ?? []);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      notify(`No se pudo cargar la lista de comprobantes con error: ${msg}`, "error");
    } finally {
      setListLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchFailed();
  }, [fetchFailed]);

  const handleRetry = useCallback(
    async (id) => {
      setRetryingId(id);
      try {
        const res = await axiosClientCreditCore.post(`/v1/siigo/outbox/${id}/retry`);
        const updated = res.data;
        if (updated?.status === "FAILED") {
          showError(updated.errorDetail || "El reintento volvió a fallar.");
          setRecords((prev) => prev.map((r) => (r.id === id ? updated : r)));
        } else {
          notify("Comprobante reenviado exitosamente.");
          setRecords((prev) => prev.filter((r) => r.id !== id));
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.response?.data || err.message;
        showError(msg);
      } finally {
        setRetryingId(null);
      }
    },
    [notify, showError]
  );

  return (
    <Section elevation={2} sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Comprobantes con error
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Estos comprobantes no se pudieron enviar a Siigo. Revisa el motivo y presiona "Reintentar"
            para volver a enviarlos.
          </Typography>
        </Box>
        <Tooltip title="Actualizar lista">
          <span>
            <IconButton onClick={fetchFailed} disabled={listLoading} sx={{ color: "#161e55" }}>
              {listLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {listLoading && records.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress sx={{ color: "#161e55" }} />
        </Box>
      ) : records.length === 0 ? (
        <Box py={4} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            🎉 No hay comprobantes con error pendientes de reintentar.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, backgroundColor: "#f5f5f5" } }}>
                <TableCell>Tipo de comprobante</TableCell>
                <TableCell align="center">Crédito</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell align="center">Fecha contable</TableCell>
                <TableCell align="center">Intentos</TableCell>
                <TableCell>Motivo del error</TableCell>
                <TableCell align="center">Registrado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <FailedOutboxRow
                  key={record.id}
                  record={record}
                  onRetry={handleRetry}
                  retrying={retryingId === record.id}
                  onShowError={showError}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {errorModal}
    </Section>
  );
}
